import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  registerUser,
  loginUser,
  fetchUserDetails,
  logoutUser,
  refreshToken,
} from "../services/authService";
import {
  getToken,
  saveToken,
  clearToken,
  isTokenExpired,
  getTokenExpiry,
  getRefreshToken,
  saveRefreshToken,
} from "../utils/tokenUtils";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // User data
  const [roles, setRoles] = useState([]); // User roles
  const [permissions, setPermissions] = useState([]); // User permissions
  const [token, setToken] = useState(null); // Auth token
  const [loading, setLoading] = useState(true); // Initial loading state
  const [authError, setAuthError] = useState(null); // Error state
  const [logoutWarning, setLogoutWarning] = useState(null); // Warning message before logout
  const isInitialized = useRef(false);

  /**
   * Log out the user.
   */
  const logout = useCallback(async () => {
    try {
      console.log("AuthContext.logout() called", { token, user });
      console.trace("AuthContext.logout() trace");
      if (token && user) {
        const response = await logoutUser(token, user.id);
        // // console.log("Logout response:", response); // Log the response for debugging
        localStorage.setItem("logoutMessage", "Logged out successfully");
        localStorage.removeItem("authToken", token);
        return response; // Return the logout response
      }
    } catch (err) {
      console.error("Error during logout:", err.message);
      return { message: err.message || "Logout failed." };
    } finally {
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setToken(null);
      clearToken();
    }
  }, [token, user]);

  /**
   * Refresh authentication token using refresh token.
   */
  const refreshAuthToken = useCallback(async () => {
    try {
      const refreshTokenValue = getRefreshToken();
      const result = await refreshToken(refreshTokenValue);
      const newToken = result.token || result.data?.token;

      if (!newToken) {
        throw new Error("No token received from refresh");
      }

      setToken(newToken);
      saveToken(newToken);

      // Save new refresh token if provided
      if (result.refresh_token || result.data?.refresh_token) {
        saveRefreshToken(result.refresh_token || result.data?.refresh_token);
      }

      return newToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      // Only logout if it's an auth error, not network error
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthError("Your session has expired. Please log in again.");
        logout();
      }
      throw err;
    }
  }, [logout]);

  /**
   * Load user details using the provided token.
   * @param {string} authToken - The token used to fetch user details.
   */
  const loadUserDetails = useCallback(
    async (authToken) => {
      try {
        setLoading(true);
        const result = await fetchUserDetails(authToken);
        setUser(result.user);
        setRoles(result.roles || []);
        setPermissions(result.permissions || []);
        setAuthError(null); // Clear any previous errors
      } catch (err) {
        console.error("Failed to load user details:", err.message);

        // Only logout on actual authentication errors (401/403), not network errors
        const isAuthError =
          err.response?.status === 401 || err.response?.status === 403;

        if (isAuthError) {
          setAuthError("Session expired. Please log in again.");
          logout();
        } else {
          // For network errors or other issues, set error but don't logout
          setAuthError(
            err.message || "Failed to load user details. Please try again.",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [logout],
  );

  /**
   * Initialize token and user details on component mount.
   */
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedToken = getToken();
    if (savedToken) {
      setToken(savedToken);
      loadUserDetails(savedToken);
    } else {
      setLoading(false); // No token, so stop loading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync logout across browser tabs.
   */
  useEffect(() => {
    const handleStorageChange = (event) => {
      console.log("AuthContext storage event:", event);
      if (event.key === "token" && !event.newValue) {
        console.log("AuthContext detected token removal via storage event");
        logout(); // Logout user if token is removed
      }
      // Also handle authToken key
      if (event.key === "authToken" && !event.newValue) {
        console.log("AuthContext detected authToken removal via storage event");
        logout();
      }
    };

    // Listen for auth:logout event from axios interceptor
    const handleAuthLogout = () => {
      setAuthError("Your session has expired. Please log in again.");
      logout();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("auth:logout", handleAuthLogout);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth:logout", handleAuthLogout);
    };
  }, [logout]);

  /**
   * Periodic token validation and refresh.
   * Validates token every 10 minutes and proactively refreshes if needed.
   */
  useEffect(() => {
    if (!token || !user) return;

    const validateToken = async () => {
      const currentToken = getToken();
      if (!currentToken) {
        // Token was removed externally
        logout();
        return;
      }

      // Check if token is expired or about to expire (2 minute buffer)
      if (isTokenExpired(2)) {
        // Token is expired, try to refresh it
        try {
          const newToken = await refreshAuthToken();
          // Optionally reload user details with new token
          await loadUserDetails(newToken);
        } catch (err) {
          // Refresh failed - if it's not already logged out, try validating with backend
          if (err.response?.status !== 401 && err.response?.status !== 403) {
            // Network error or other issue - try validating existing token
            try {
              await fetchUserDetails(currentToken);
              setToken(currentToken);
            } catch (validateErr) {
              if (
                validateErr.response?.status === 401 ||
                validateErr.response?.status === 403
              ) {
                setAuthError("Your session has expired. Please log in again.");
                logout();
              }
            }
          }
          // If it's 401/403, logout was already called in refreshAuthToken
        }
      } else {
        // Token is still valid, check if we should refresh proactively
        const expiry = getTokenExpiry();
        if (expiry) {
          const timeUntilExpiry = expiry - Date.now();
          // If token expires in less than 15 minutes, proactively refresh
          if (timeUntilExpiry < 15 * 60 * 1000 && timeUntilExpiry > 0) {
            try {
              await refreshAuthToken();
            } catch (err) {
              // Silent fail - don't logout on proactive refresh failure
              console.warn("Proactive token refresh failed:", err.message);
            }
          }
        }
      }
    };

    // Validate immediately
    validateToken();

    // Then validate every 10 minutes (less aggressive than 5 minutes)
    const interval = setInterval(validateToken, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, user, logout, loadUserDetails, refreshAuthToken]);

  /**
   * Show user warning before session expires.
   */
  useEffect(() => {
    if (!token || !user) {
      setLogoutWarning(null);
      return;
    }

    const checkExpiry = () => {
      const expiry = getTokenExpiry();
      if (!expiry) {
        setLogoutWarning(null);
        return;
      }

      const timeUntilExpiry = expiry - Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      const oneMinute = 60 * 1000;

      if (timeUntilExpiry > 0 && timeUntilExpiry <= fiveMinutes) {
        if (timeUntilExpiry <= oneMinute) {
          setLogoutWarning("Your session will expire in less than 1 minute.");
        } else {
          const minutes = Math.ceil(timeUntilExpiry / 60000);
          setLogoutWarning(`Your session will expire in ${minutes} minute(s).`);
        }
      } else {
        setLogoutWarning(null);
      }
    };

    checkExpiry();
    // Check every 30 seconds for warnings
    const interval = setInterval(checkExpiry, 30000);
    return () => clearInterval(interval);
  }, [token, user]);

  /**
   * Register a new user.
   * @param {object} data - Registration data.
   * @returns {Promise<object>} - The registration result.
   */
  const register = useCallback(async (data) => {
    try {
      const result = await registerUser(data);
      setUser(result.user);
      const authToken = result.token || result.data?.token;
      setToken(authToken);
      saveToken(authToken);

      // Save refresh token if provided
      if (result.refresh_token || result.data?.refresh_token) {
        saveRefreshToken(result.refresh_token || result.data?.refresh_token);
      }

      setAuthError(null);
      return result;
    } catch (err) {
      console.error("Error during registration:", err.message);
      setAuthError(err.message || "Registration failed.");
      throw err;
    }
  }, []);

  /**
   * Log in a user.
   * @param {object} data - Login data.
   * @returns {Promise<object>} - The login result.
   */
  const login = useCallback(
    async (data) => {
      try {
        const result = await loginUser(data);
        setUser(result.user);
        const authToken = result.token || result.data?.token;
        setToken(authToken);
        saveToken(authToken);

        // Save refresh token if provided
        if (result.refresh_token || result.data?.refresh_token) {
          saveRefreshToken(result.refresh_token || result.data?.refresh_token);
        }

        setAuthError(null);
        await loadUserDetails(authToken); // Load user details to populate context
        return result;
      } catch (err) {
        console.error("Error during login:", err.message);
        setAuthError(
          err.message || "Login failed. Please check your credentials.",
        );
        throw err;
      }
    },
    [loadUserDetails],
  );

  /**
   * Check if user has a specific permission.
   */
  const hasPermission = useCallback(
    (permission) => {
      return permissions.includes(permission);
    },
    [permissions],
  );

  /**
   * Memoize the auth context value to optimize re-renders.
   */
  const authValue = useMemo(
    () => ({
      loadUserDetails,
      user,
      roles,
      permissions,
      hasPermission,
      token,
      loading,
      authError,
      logoutWarning,
      dismissLogoutWarning: () => setLogoutWarning(null),
      register,
      login,
      logout,
    }),
    [
      loadUserDetails,
      user,
      roles,
      permissions,
      hasPermission,
      token,
      loading,
      authError,
      logoutWarning,
      register,
      login,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
